import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { isApiError } from '../api/client';
import {
  fetchOlderMessages,
  useEmployee,
  useInterruptTurn,
  usePatchThread,
  usePendingApprovals,
  useResolveApproval,
  useSendTurn,
  useThread,
} from '../api/hooks';
import { AppShell } from '../app/AppShell';
import { useSession } from '../app/SessionProvider';
import { IconBack } from '../components/icons';
import { Button, EmptyState, Field, Sheet, Spinner, TextInput } from '../components/ui';
import { zh } from '../i18n';
import { PROMPT_MAX_CHARS } from '../lib/limits';
import { localId } from '../lib/localId';
import { useStream } from '../stream/StreamProvider';
import { notifyApproval, requestPermissionAfterFirstResolution } from '../stream/notifications';
import { selectThread, type MessageItem, type ToolItem } from '../stream/streamReducer';
import type { ApprovalResolveInput } from './ApprovalCard';
import { ThreadsPane } from '../threads/ThreadsPane';
import { ChatTranscript } from './ChatTranscript';
import { Composer } from './Composer';
import { buildRows } from './grouping';

/**
 * One thread (SPEC §10).
 *
 * Content arrives only over SSE (CONTRACTS §4.3: the POST is fire-and-forget),
 * so the user's own message is echoed locally and REST hydration is an OVERLAY
 * rather than a replace — otherwise a mid-turn refetch would wipe the text
 * currently streaming, which Tier A has not mirrored yet.
 */
export function ChatRoute(): JSX.Element {
  const { threadId } = useParams<{ threadId: string }>();
  const { state, dispatch } = useStream();
  const session = useSession();

  const threadQuery = useThread(threadId);
  const approvalsQuery = usePendingApprovals(Boolean(threadId));
  const employee = useEmployee();
  const sendTurn = useSendTurn();
  const interrupt = useInterruptTurn();
  const resolveApproval = useResolveApproval();
  const patchThread = usePatchThread();

  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [blockedReason, setBlockedReason] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [olderExhausted, setOlderExhausted] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const notifiedRef = useRef<Set<string>>(new Set());
  const resolvedOnceRef = useRef(false);

  const thread = selectThread(state, threadId);

  // Hydrate from REST whenever the payload changes (initial load, resync).
  useEffect(() => {
    if (!threadId || !threadQuery.data) return;
    dispatch({ type: 'hydrate', threadId, detail: threadQuery.data });
  }, [threadId, threadQuery.data, dispatch]);

  // CONTRACTS §5: a `resync` frame means the ring could not replay. Refetch
  // both halves the contract names — the thread and the pending approvals.
  const resyncNonce = state.resyncNonce;
  const refetchThread = threadQuery.refetch;
  const refetchApprovals = approvalsQuery.refetch;
  useEffect(() => {
    if (resyncNonce === 0) return;
    void refetchThread();
    void refetchApprovals();
  }, [resyncNonce, refetchThread, refetchApprovals]);

  // Local notification when an approval lands while the tab is hidden.
  useEffect(() => {
    for (const approval of state.pendingApprovals) {
      if (notifiedRef.current.has(approval.id)) continue;
      notifiedRef.current.add(approval.id);
      notifyApproval(approval);
    }
  }, [state.pendingApprovals]);

  const rows = useMemo(() => buildRows(thread), [thread]);

  const toolsByTurn = useMemo(() => {
    const map: Record<string, ToolItem[]> = {};
    for (const id of thread.order) {
      const item = thread.byId[id];
      if (item?.kind !== 'tool') continue;
      const bucket = map[item.turnId];
      if (bucket) bucket.push(item);
      else map[item.turnId] = [item];
    }
    return map;
  }, [thread]);

  const oldestSeq = useMemo(() => {
    let lowest: number | null = null;
    for (const id of thread.order) {
      const item = thread.byId[id];
      if (item?.kind !== 'message' || item.pending) continue;
      const message = item as MessageItem;
      if (lowest === null || message.seq < lowest) lowest = message.seq;
    }
    return lowest;
  }, [thread]);

  const activeTurnId = thread.activeTurnId;
  const running = activeTurnId !== null;

  const disabledReason = useMemo(() => {
    if (running) return null;
    if (state.connection === 'offline') return zh.chat.disabledOffline;
    if (session.maintenance) return zh.chat.disabledMaintenance;
    const credentialState = session.credential?.state;
    if (credentialState === 'absent' || credentialState === 'invalid') {
      return zh.chat.disabledNoCredential;
    }
    if (session.runnerState === 'creating' || session.runnerState === 'starting') {
      return zh.chat.disabledStarting;
    }
    return null;
  }, [running, state.connection, session.maintenance, session.credential, session.runnerState]);

  const onSend = useCallback(
    (text: string) => {
      if (!threadId) return;
      setBlockedReason(null);
      const id = localId('local');
      dispatch({
        type: 'localMessage',
        message: {
          threadId,
          id,
          text,
          seq: Number.MAX_SAFE_INTEGER,
          turnId: null,
          createdAt: Date.now(),
        },
      });
      sendTurn.mutate(
        { threadId, text },
        {
          onError: (error) => {
            dispatch({ type: 'localMessageFailed', threadId, id });
            if (isApiError(error, 'DAILY_BUDGET_EXCEEDED')) setBlockedReason(zh.chat.disabledBudget);
            else if (isApiError(error, 'AWAITING_CREDENTIAL'))
              setBlockedReason(zh.chat.disabledNoCredential);
            else if (isApiError(error, 'TURN_IN_PROGRESS'))
              setBlockedReason(zh.chat.disabledTurnRunning);
            else if (isApiError(error, 'MAINTENANCE'))
              setBlockedReason(zh.chat.disabledMaintenance);
            else setBlockedReason(error instanceof Error ? error.message : zh.app.unknownError);
          },
        },
      );
    },
    [threadId, dispatch, sendTurn],
  );

  const onStop = useCallback(() => {
    if (!threadId) return;
    interrupt.mutate(threadId);
  }, [threadId, interrupt]);

  const onResolveApproval = useCallback(
    (approvalId: string, input: ApprovalResolveInput) => {
      setResolvingId(approvalId);
      resolveApproval.mutate(
        { id: approvalId, ...input },
        {
          onSettled: () => setResolvingId(null),
          onSuccess: () => {
            if (!resolvedOnceRef.current) {
              resolvedOnceRef.current = true;
              requestPermissionAfterFirstResolution();
            }
          },
          onError: (error) => {
            // 410 gone / expired and 409 already-resolved both mean the same
            // thing to the user: this card can no longer be acted on.
            if (isApiError(error, 'APPROVAL_GONE') || isApiError(error, 'ALREADY_RESOLVED')) {
              dispatch({ type: 'approvalGone', approvalId });
            }
          },
        },
      );
    },
    [resolveApproval, dispatch],
  );

  const onLoadOlder = useCallback(() => {
    if (!threadId || oldestSeq === null) return;
    setLoadingOlder(true);
    void fetchOlderMessages(threadId, oldestSeq)
      .then((page) => {
        dispatch({
          type: 'prepend',
          threadId,
          messages: page.items,
          toolCalls: page.toolCalls,
        });
        if (!page.hasMore) setOlderExhausted(true);
      })
      .catch(() => setOlderExhausted(true))
      .finally(() => setLoadingOlder(false));
  }, [threadId, oldestSeq, dispatch]);

  if (!threadId) return <AppShell>{null}</AppShell>;

  const title = threadQuery.data?.thread.title ?? zh.threads.untitled;
  const canLoadOlder = !olderExhausted && oldestSeq !== null && oldestSeq > 1;

  return (
    <AppShell
      title={title}
      scrollable={false}
      leading={
        <Link to="/" className="btn btn--ghost btn--sm" aria-label={zh.chat.backToList}>
          <IconBack size={18} />
        </Link>
      }
      trailing={
        <Button variant="ghost" small onClick={() => setRenaming(title)}>
          {zh.threads.rename}
        </Button>
      }
    >
      {/*
        SPEC §10's two panes. `.panes` is `display: contents` below 1024 px, so
        a phone lays the conversation out exactly as it did before the wrapper
        existed and the list pane is not drawn at all.
      */}
      <div className="panes panes--detail">
        <div className="panes__list">
          <ThreadsPane />
        </div>
        <div className="panes__detail">
          <div className="chat">
            {threadQuery.isLoading && rows.length === 0 ? (
              <Spinner />
            ) : rows.length === 0 ? (
              <EmptyState title={zh.chat.emptyTitle} body={zh.chat.emptyBody} />
            ) : (
              <ChatTranscript
                rows={rows}
                toolsByTurn={toolsByTurn}
                onResolveApproval={onResolveApproval}
                resolvingApprovalId={resolvingId}
                maxTurns={employee.data?.maxTurns ?? null}
                maxBudgetUsd={employee.data?.maxBudgetUsd ?? null}
                budgetsEnforced={session.budgetsEnforced}
                onLoadOlder={canLoadOlder ? onLoadOlder : null}
                loadingOlder={loadingOlder}
              />
            )}

            {rows.length === 0 && !threadQuery.isLoading ? (
              <div className="chips" style={{ marginBottom: '0.5rem' }}>
                {zh.chat.starters.map((starter) => (
                  <Button key={starter} variant="outline" small onClick={() => onSend(starter)}>
                    {starter}
                  </Button>
                ))}
              </div>
            ) : null}

            <Composer
              onSend={onSend}
              onStop={onStop}
              running={running}
              disabledReason={disabledReason}
              noticeText={blockedReason}
              maxChars={PROMPT_MAX_CHARS}
              busy={sendTurn.isPending || interrupt.isPending}
            />
          </div>
        </div>
      </div>

      {renaming !== null ? (
        <Sheet title={zh.threads.renameTitle} onClose={() => setRenaming(null)}>
          <div className="stack">
            <Field label={zh.threads.renameTitle}>
              {(id) => (
                <TextInput
                  id={id}
                  value={renaming}
                  maxLength={120}
                  onChange={(event) => setRenaming(event.target.value)}
                />
              )}
            </Field>
            <Button
              variant="primary"
              disabled={patchThread.isPending || renaming.trim().length === 0}
              onClick={() => {
                patchThread.mutate(
                  { id: threadId, title: renaming.trim() },
                  { onSuccess: () => setRenaming(null) },
                );
              }}
            >
              {patchThread.isPending ? zh.app.saving : zh.app.save}
            </Button>
          </div>
        </Sheet>
      ) : null}
    </AppShell>
  );
}
